import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import DoneIcon from "@mui/icons-material/Done";
import EditIcon from "@mui/icons-material/Edit";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import {
  Autocomplete,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ConfirmationModal from "common/components/ConfirmationModal";
import useDynamicField from "developer/edit/hooks/useDynamicField";
import useFieldNameSuggestions from "developer/edit/hooks/useFieldNameSuggestions";
import React, { useCallback, useEffect, useMemo } from "react";
import FieldOptionsBasicForm from "./FieldOptionsBasicForm";
import { ChildSelectConfig, SelectOption } from "./OptionsSection";

type FieldOptionsAdvancedFormProps = {
  keySelected: string | null;
  setKeySelected: React.Dispatch<React.SetStateAction<string | null>>;
  selectOptions: ChildSelectConfig;
  onChange: (newSelectOptions: ChildSelectConfig) => void;
  isShowLabelsEnabled: boolean;
};

const FieldOptionsAdvancedForm: React.FC<FieldOptionsAdvancedFormProps> = ({
  keySelected,
  setKeySelected,
  selectOptions,
  onChange,
  isShowLabelsEnabled,
}) => {
  const [isEditSelected, setIsEditSelected] = React.useState(false);
  const [isDeleteSelected, setIsDeleteSelected] = React.useState(false);
  const [value, setValue] = React.useState("");
  const [dependsOnError, setDependsOnError] = React.useState<string>("");

  const fieldNameSuggestions = useFieldNameSuggestions();

  const {
    input: { value: fieldName },
  } = useDynamicField<string>("element.name");

  const keys = useMemo(() => {
    return Object.keys(selectOptions.childOptions ?? {});
  }, [selectOptions]);

  useEffect(() => {
    if (keys.length > 0 && !keySelected) setKeySelected(keys[0]);
  }, [keys, keySelected, setKeySelected]);

  const fieldNameSuggestionsWithoutCurrentField = useMemo(() => {
    return fieldNameSuggestions.filter((x) => x !== fieldName);
  }, [fieldName, fieldNameSuggestions]);

  const [isAddSelected, setIsAddSelected] = React.useState(
    !keys || keys.length === 0
  );

  const handleClickAddNewKey = (newKeyName: string) => {
    const newSelectOptions = structuredClone(selectOptions);

    const newOptions = Array.from({ length: 2 }, (_, i) => ({
      value: `Option ${i + 1}`,
    }));

    newSelectOptions.childOptions[newKeyName] = newOptions;
    onChange(newSelectOptions);
  };

  const handleClickEditKey = (oldKeyName: string, newKeyName: string) => {
    const newSelectOptions = structuredClone(selectOptions);
    const keySelectOptions = selectOptions.childOptions[oldKeyName];
    newSelectOptions.childOptions[newKeyName] = keySelectOptions;

    delete newSelectOptions.childOptions[oldKeyName];
    onChange(newSelectOptions);
  };

  const handleClickDeleteKey = () => {
    if (isDeleteSelected && keySelected) {
      const newSelectOptions = structuredClone(selectOptions);
      delete newSelectOptions.childOptions[keySelected];

      const newKeys = Object.keys(newSelectOptions.childOptions);

      onChange(newSelectOptions);

      if (newKeys.length === 0) {
        setIsAddSelected(true);
      }

      setIsDeleteSelected(false);
      setKeySelected(null);
      setValue("");
    }
  };

  const handleClickDoneIcon = () => {
    if (isAddSelected && value) {
      handleClickAddNewKey(value);
      setKeySelected(value);
      setIsAddSelected(false);
    }
    if (isEditSelected && value && keySelected) {
      handleClickEditKey(keySelected, value);
      setKeySelected(value);
      setIsEditSelected(false);
    }

    setValue("");
  };

  const handleClickCancelIcon = () => {
    setIsAddSelected(false);
    setIsEditSelected(false);
    setIsDeleteSelected(false);
    setValue("");
  };

  const isValidFieldName = (fieldName: string) => {
    // Verificar si el nombre contiene solo caracteres alfanuméricos y guiones bajos
    return /^[a-zA-Z0-9_]+$/.test(fieldName);
  };

  const handleChangeDependsOn = (event: any, newValue: string | null) => {
    const newSelectOptions = structuredClone(selectOptions);

    if (newValue) {
      const isValid = isValidFieldName(newValue);

      if (isValid) {
        setDependsOnError("");
        onChange(newSelectOptions);
      } else {
        setDependsOnError(
          "Only alphanumeric characters and underscores are allowed"
        );
        return;
      }

      newSelectOptions.dependsOn = newValue;
    } else {
      newSelectOptions.dependsOn = "";
      setDependsOnError("");
    }
  };

  const handleBlurDependsOn = (event: any) => {
    setDependsOnError("");
  };

  const KeyActions = () => {
    if (isAddSelected || isEditSelected)
      return (
        <>
          <Tooltip title="Done">
            <IconButton onClick={handleClickDoneIcon}>
              <DoneIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel">
            <IconButton onClick={handleClickCancelIcon}>
              <HighlightOffIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      );

    return (
      <Stack direction="row" spacing={0}>
        <Tooltip title="Add New Value">
          <IconButton size="small" onClick={() => setIsAddSelected(true)}>
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {keySelected && (
          <>
            <Tooltip title="Edit Value">
              <IconButton
                size="small"
                onClick={() => {
                  setValue(keySelected);
                  setIsEditSelected(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete Value">
              <IconButton
                size="small"
                onClick={() => setIsDeleteSelected(true)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
      </Stack>
    );
  };

  const handleChange = useCallback(
    (newSelectedOptions: SelectOption[]) => {
      if (!keySelected) return;

      const newOptions = structuredClone(selectOptions);
      newOptions.childOptions[keySelected] = newSelectedOptions;

      onChange(newOptions);
    },
    [keySelected, onChange, selectOptions]
  );

  const showKeyNameTextField = isAddSelected || isEditSelected;
  const showBasicOptionsTable =
    keySelected && !!keys.find((key) => key === keySelected);

  return (
    <Stack spacing={1}>
      <Autocomplete
        options={fieldNameSuggestionsWithoutCurrentField}
        value={selectOptions.dependsOn}
        onChange={handleChangeDependsOn}
        onBlur={handleBlurDependsOn}
        renderInput={(params) => (
          <TextField
            {...params}
            label="When Field"
            error={!!dependsOnError}
            helperText={dependsOnError}
          />
        )}
      />
      <Stack direction="row" spacing={0} width={1} alignItems="flex-end">
        {showKeyNameTextField ? (
          <TextField
            value={value}
            onChange={(e) => setValue(e.target.value)}
            sx={{ width: "100%", maxWidth: 200 }}
            label={isAddSelected ? "New Value" : "Edit Value"}
          />
        ) : (
          <TextField
            select
            label="Has Value"
            InputLabelProps={{ shrink: keySelected ? true : false }}
            sx={{ width: "100%", maxWidth: 200 }}
            value={keySelected}
            onChange={(e) => setKeySelected(e.target.value)}
          >
            {keys.map((key) => (
              <MenuItem key={key} value={key}>
                {key}
              </MenuItem>
            ))}
          </TextField>
        )}
        <KeyActions />
      </Stack>
      {showBasicOptionsTable && (
        <FieldOptionsBasicForm
          selectOptions={selectOptions.childOptions[keySelected]}
          onChange={handleChange}
          isShowLabelsEnabled={isShowLabelsEnabled}
        />
      )}
      <ConfirmationModal
        cancelLabel="Cancel"
        open={!!isDeleteSelected}
        onCancel={() => setIsDeleteSelected(false)}
        onConfirm={() => handleClickDeleteKey()}
        confirmLabel="Yes, Delete"
        title="Are you sure?"
        mainButtonType="error"
        children={
          <Typography
            variant="body1"
            color="initial"
          >{`The options of Key '${keySelected}' will be deleted too.`}</Typography>
        }
      />
    </Stack>
  );
};

export default FieldOptionsAdvancedForm;
